import { supabase } from './supabaseClient';
import { AAD_TRUSTED_CONTACT_V1, decryptData } from './cryptoService';
import { aad as beneficiaryAad } from './beneficiaryService';

const RATE_LIMIT_HOURS = 24;
const VAULT_ITEM_AAD_PREFIX = 'safeheir-vault-item-v1|';

/**
 * Export RGPD art.20 des données utilisateur.
 * Déchiffre côté client, log dans audit_logs, applique rate limit 24h.
 *
 * @param {Object} params
 * @param {string} params.userId
 * @param {CryptoKey} params.masterKey
 * @returns {Promise<{success: boolean, data?: object, error?: string, retryAfterHours?: number}>}
 */
export async function exportUserData({ userId, masterKey }) {
  // 1. Filet MK
  if (!masterKey) {
    return { success: false, error: 'vault_locked' };
  }

  // 2. Check rate limit
  const { data: lastExport, error: rateLimitErr } = await supabase
    .from('audit_logs')
    .select('created_at')
    .eq('user_id', userId)
    .eq('event', 'user_data_export')
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (rateLimitErr) {
    console.error('[exportService] rate limit check failed:', rateLimitErr);
    // On continue quand même — mieux vaut permettre un export que le bloquer sur bug audit
  }

  if (lastExport) {
    const hoursSinceLast = (Date.now() - new Date(lastExport.created_at).getTime()) / 3_600_000;
    if (hoursSinceLast < RATE_LIMIT_HOURS) {
      return {
        success: false,
        error: 'rate_limited',
        retryAfterHours: Math.ceil(RATE_LIMIT_HOURS - hoursSinceLast),
      };
    }
  }

  // 3. Récupération email (auth)
  const { data: { user }, error: authErr } = await supabase.auth.getUser();
  if (authErr || !user) {
    return { success: false, error: 'unknown' };
  }
  const userEmail = user.email;

  // 4. Fetch parallèle 4 tables (RLS auto)
  const [profileRes, vaultRes, beneRes, checkinRes] = await Promise.all([
    supabase.from('user_profiles').select('id, full_name, first_name, last_name, is_premium, onboarding_completed, created_at, updated_at').eq('id', userId).maybeSingle(),
    supabase.from('vault_items').select('id, category, iv, encrypted_data, created_at, updated_at').eq('user_id', userId),
    supabase.from('beneficiaries').select('id, priority, is_active, status, access_categories, iv, encrypted_data, designation_consent_at, designation_form_version, responded_at, created_at, updated_at, designator_consent_at, designator_consent_version, designator_consent_text_hash, release_path, key_version, revoked_at, revoked_reason, transmitted_at, trusted_contact_encrypted, trusted_contact_iv, trusted_contact_notified_at').eq('user_id', userId),
    supabase.from('checkin_config').select('frequency, grace_period_days, is_enabled, custom_message, last_check_in, next_check_in_due, reminder_count, last_reminder_at, vacation_return_date, updated_at').eq('user_id', userId).maybeSingle(),
  ]);

  const errors = [];

  // 5. Déchiffrement vault_items
  const vaultItems = [];
  for (const item of vaultRes.data || []) {
    try {
      const aad = `${VAULT_ITEM_AAD_PREFIX}${item.id}`;
      const plaintext = await decryptData(item.encrypted_data, item.iv, masterKey, aad);
      vaultItems.push({
        id: item.id,
        category: item.category,
        created_at: item.created_at,
        updated_at: item.updated_at,
        data: plaintext,
      });
    } catch (err) {
      errors.push({ type: 'vault_item_decrypt_failed', id: item.id, reason: err.message });
    }
  }

  // 6. Déchiffrement beneficiaries
  const beneficiaries = [];
  for (const bene of beneRes.data || []) {
    try {
      const aad = beneficiaryAad(bene.id);
      const plaintext = await decryptData(bene.encrypted_data, bene.iv, masterKey, aad);

      // PF-03 CP3.2 : contact de confiance déchiffré ; échec consigné sans exclure le bénéficiaire.
      let trustedContact = null;
      if (bene.trusted_contact_encrypted && bene.trusted_contact_iv) {
        try {
          trustedContact = await decryptData(
            bene.trusted_contact_encrypted,
            bene.trusted_contact_iv,
            masterKey,
            AAD_TRUSTED_CONTACT_V1(bene.id)
          );
        } catch (contactErr) {
          errors.push({ type: 'trusted_contact_decrypt_failed', id: bene.id, reason: contactErr.message });
        }
      }

      beneficiaries.push({
        id: bene.id,
        priority: bene.priority,
        is_active: bene.is_active,
        status: bene.status,
        access_categories: bene.access_categories,
        designation_consent_at: bene.designation_consent_at,
        designation_form_version: bene.designation_form_version,
        designator_consent_at: bene.designator_consent_at,
        designator_consent_version: bene.designator_consent_version,
        designator_consent_text_hash: bene.designator_consent_text_hash,
        responded_at: bene.responded_at,
        release_path: bene.release_path,
        key_version: bene.key_version,
        revoked_at: bene.revoked_at,
        revoked_reason: bene.revoked_reason,
        transmitted_at: bene.transmitted_at,
        trusted_contact: trustedContact,
        trusted_contact_notified_at: bene.trusted_contact_notified_at,
        created_at: bene.created_at,
        updated_at: bene.updated_at,
        data: plaintext,
      });
    } catch (err) {
      errors.push({ type: 'beneficiary_decrypt_failed', id: bene.id, reason: err.message });
    }
  }

  // 7. Construction JSON export
  const exportData = {
    export_metadata: {
      exported_at: new Date().toISOString(),
      format_version: '1.0',
      source: 'SafeHeir',
      rgpd_article: '20',
    },
    account: {
      id: userId,
      email: userEmail,
      first_name: profileRes.data?.first_name || null,
      last_name: profileRes.data?.last_name || null,
      full_name: profileRes.data?.full_name || null,
      is_premium: profileRes.data?.is_premium || false,
      onboarding_completed: profileRes.data?.onboarding_completed || false,
      created_at: profileRes.data?.created_at || null,
      updated_at: profileRes.data?.updated_at || null,
    },
    vault_items: vaultItems,
    beneficiaries,
    checkin_config: checkinRes.data || null,
    errors,
  };

  // 8. Log dans audit_logs (bonus, non bloquant)
  const { error: logErr } = await supabase.from('audit_logs').insert({
    user_id: userId,
    event: 'user_data_export',
    metadata: {
      vault_items_count: vaultItems.length,
      beneficiaries_count: beneficiaries.length,
      errors_count: errors.length,
    },
  });
  if (logErr) {
    console.warn('[exportService] audit_logs INSERT failed, rate limit ne sera pas enforçable pour cet export:', logErr);
  }

  return { success: true, data: exportData };
}
